import klass from './Tabs.module.scss';
import { type Html, html, rune, View } from 'rune-ts';
import { staticTypo, typo, typoPcMo } from '../../../../shared/typography/typo';
import { classes } from '../../../../shared/util';
import { TabChangeEvent } from './events';
import { UtilF } from '../../../../../../modules/Util/F/Function/module/UtilF';
import { font } from '../../../../shared/typography';

export enum TabVariants {
  background = 'background',
}

export type TabType = {
  id: string;
  href?: string;
  label: string;
  klass?: string;
  mo_no_padding?: boolean;
  mo_no_underline?: boolean;
  data?: {
    count: number;
  };
};

export interface TabProps {
  tab: TabType;
  reload: boolean;
  size?: 'large' | 'medium' | 'small';
  active: boolean;
  hashKey?: string;
}

export interface TabsProps {
  current_tab_id: string;
  tabs: TabType[];
  size?: 'large' | 'medium' | 'small';
  useHash?: boolean; // 아직 구현 안함
  hashKey?: string;
  reload?: boolean;
  color_invert?: boolean;
  custom_klass?: string;
  mobile_circle?: boolean;
  mo_no_underline?: boolean;
  variants?: TabVariants;
}

class Tab extends View<TabProps> {
  protected override template(data: TabProps): Html {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    const { tab, size, active, hashKey = 'tab' } = data;
    const is_link = !!tab.href;
    const href = tab.href || `?${hashKey}=${tab.id}`;

    const typography =
      size === 'small'
        ? typoPcMo({ is_mobile, pc: '16_medium', mo: active ? '14_bold' : '14_medium' })
        : size === 'medium'
          ? font('28_bold')
          : staticTypo('bebas_40_bold');

    const count_typography = is_mobile
      ? active
        ? staticTypo('unica_14_bold')
        : staticTypo('unica_14_medium')
      : typo('16_medium');

    return html`
      <${is_link ? 'a' : 'span'}
        ${is_link ? html.preventEscape(`href="${href}"`) : ``}
        data-id="${tab.id}"
        data-is_link="${is_link}"
        class="${classes(klass.tab, typography, tab.klass, {
          [klass.active]: active,
          [klass.mo_no_padding]: tab.mo_no_padding,
          [klass.mo_no_underline]: tab.mo_no_underline,
        })}"
      >
        <!-- label -->
        ${tab.label}

        <!-- count -->
        ${
          tab.data
            ? html`<span class="${klass.count} ${count_typography}">${tab.data.count.toLocaleString()}</span>`
            : ''
        }
      </${is_link ? 'a' : 'span'}>
    `;
  }
}

export class Tabs extends View<TabsProps> {
  tabViews: Tab[] = [];
  constructor(props: TabsProps) {
    super(props);
    const { reload = true, size = 'small', hashKey, current_tab_id } = props;

    this.tabViews = props.tabs.map((tab) => {
      if (props.variants === TabVariants.background) {
        tab = {
          ...tab,
          klass: tab.klass + ' ' + klass.background,
        };
      }
      return new Tab({ reload, tab, hashKey, size, active: tab.id === current_tab_id });
    });
  }

  protected override template(data: TabsProps) {
    const { size = 'small', custom_klass = '', mobile_circle, color_invert } = data;
    return html`<div
      class="${classes(klass.tabs, size, custom_klass, {
        [klass.circle]: mobile_circle,
        [klass.invert]: color_invert,
        [klass.mo_no_underline]: this.data.mo_no_underline,
      })}"
    >
      ${this.tabViews}
    </div> `;
  }

  scrollIntoView(option: ScrollIntoViewOptions = { behavior: 'smooth', block: 'nearest', inline: 'center' }) {
    const element = this.element().querySelector(`.${klass.active}`);
    if (element) {
      element.scrollIntoView(option);
    }
  }

  protected override onMount() {
    const { reload } = this.data;

    UtilF.initBackForward(() => {
      this.redraw();
    });

    this.delegate('click', `.${klass.tab}`, (e) => {
      const target = (e.currentTarget as HTMLAnchorElement)!;
      const is_link = target.dataset.is_link === 'true';

      if (target?.classList.contains(klass.active)) return e.originalEvent.preventDefault();

      if (reload || is_link) return;
      this.element().querySelector(`[data-id="${this.data.current_tab_id}"]`)?.classList.remove(klass.active);
      this.element().querySelector(`[data-id="${target.dataset.id}"]`)?.classList.add(klass.active);
      this.data.current_tab_id = target.dataset.id!;
      this.onTabChanged();
    });
  }

  protected onTabChanged(): this {
    return this.dispatchEvent(TabChangeEvent, {
      bubbles: true,
      detail: {
        tabId: this.data.current_tab_id,
      },
    });
  }
}
